import { putData } from '../backend';
import { Document, Workflow } from '../interfaces';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from '../contexts';
import Modal from './Modal';
import { KeyedMutator } from "swr";
import { useTranslation } from 'react-i18next';
import SmallSelectField from './SmallSelectField';
import { GOOGLE_TO_MICROSOFT_EXT_MAP, GOOGLE_TO_MICROSOFT_MIMETYPE_MAP, MIMETYPE_ICON_MAP } from '../utils';

interface Props {
  workflow: Workflow;
  mutateWorkflow: KeyedMutator<Workflow>;
}

function WorkflowOutputHubspotModal(props: Props) {
  const { workflow, mutateWorkflow } = props;
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const history = useHistory();

  function goToDocumentBlock(index: number) {
    closeModal();
    history.push(`/w/${workflow.id}/document` + (index > 0 ? `#${index + 1}` : ""));
  }

  const [documents, setDocuments] = useState<Document[]>(workflow.documents);

  async function updateDocument(document: Document, field: string, value: any) {
    const updatedDocument = { ...document, [field]: value };
    setDocuments(docs => docs.map(d => d.id === document.id ? updatedDocument : d));
    await putData(`/workflows/documents/${document.id}/`, updatedDocument)
      .then(() => mutateWorkflow())
      .catch(console.error);
  }

  return (
    <Modal
      title={t("Upload Outputs to HubSpot")}
      primaryButtonProps={{
        text: t("Done"),
        onClick: closeModal,
      }}
    >
      <div className='text-sm font-semibold text-gray-500'>
        {t("Upload PDF and Microsoft file copies to HubSpot and attach them to the associated record.")}
      </div>

      <div className="flex flex-col">
        {documents.map((document, index) => {
          const isGoogleType = document.documentType.startsWith("GOOGLE");
          const isMicrosoftType = document.documentType.startsWith("MICROSOFT");
          const isGoogleOnly = isGoogleType && !document.createPdfCopy && !document.createMicrosoftCopy && !document.isSignable;
          const isFillablePdf = document.documentType === "PDF_FILLABLE";
          const rows = [];

          if (isGoogleType) {
            if (document.createMicrosoftCopy) {
              rows.push({ key: `${document.id}-ms`, icon: MIMETYPE_ICON_MAP[GOOGLE_TO_MICROSOFT_MIMETYPE_MAP[document.documentType]], name: `${document.outputName}.${GOOGLE_TO_MICROSOFT_EXT_MAP[document.documentType]?.toLowerCase()}` });
            }
            if (document.createPdfCopy || document.isSignable) {
              rows.push({ key: `${document.id}-pdf`, icon: MIMETYPE_ICON_MAP["application/pdf"], name: `${document.outputName}.pdf` });
            }
          } else if (isMicrosoftType) {
            rows.push({ key: `${document.id}-ms`, icon: MIMETYPE_ICON_MAP[GOOGLE_TO_MICROSOFT_MIMETYPE_MAP[document.documentType]], name: `${document.outputName}.${GOOGLE_TO_MICROSOFT_EXT_MAP[document.documentType]?.toLowerCase()}` });
            if (document.createPdfCopy || document.isSignable) {
              rows.push({ key: `${document.id}-pdf`, icon: MIMETYPE_ICON_MAP["application/pdf"], name: `${document.outputName}.pdf` });
            }
          } else if (isFillablePdf) {
            rows.push({ key: `${document.id}-pdf`, icon: MIMETYPE_ICON_MAP["application/pdf"], name: `${document.outputName}.pdf` });
          }

          return (
            <div key={document.id} className='flex flex-col py-3 border-b border-gray-200'>
              <div className="flex items-start gap-1.5 ">
                <div className="flex flex-col gap-1.5 w-[520px]">
                  <div className="flex items-center gap-2">
                    <label className="flex items-start gap-3 cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        className="w-4 h-4 flex-shrink-0 accent-blue"
                        checked={!isGoogleOnly && document.uploadToHubspot}
                        disabled={isGoogleOnly}
                        onChange={(e) => updateDocument(document, "uploadToHubspot", e.target.checked)}
                      />
                      <span className="text-sm font-gilroy font-semibold text-gray-600 truncate w-[520px]">{document.file?.name ?? document.outputName}</span>
                    </label>
                  </div>
                  {rows.map((row) => (
                    <div key={row.key} className="flex items-center gap-1.5 ml-7">
                      <img className="w-4 h-4 flex-shrink-0" src={row.icon} alt="File icon" />
                      <span className="text-xs font-gilroy font-semibold text-gray-600 truncate">{row.name}</span>
                    </div>
                  ))}
                </div>
                {!isGoogleOnly &&
                  <div className="ml-auto flex-shrink-0">
                    <SmallSelectField label={t("File Access")} value={document.uploadToHubspotAccess} onChange={(value) => updateDocument(document, "uploadToHubspotAccess", value)} disabled={!document.uploadToHubspot || isGoogleOnly}>
                      <option value="PRIVATE">{t("Private")}</option>
                      <option value="PUBLIC_INDEXABLE">{t("Public (Indexable)")}</option>
                      <option value="PUBLIC_NOT_INDEXABLE">{t("Public (Not Indexable)")}</option>
                    </SmallSelectField>
                  </div>
                }
              </div>
              {isGoogleOnly &&
                <div className='text-xs text-yellow font-semibold mt-2 ml-7 underline cursor-pointer hover:text-yellow-600' onClick={() => goToDocumentBlock(index)}>
                  {t("Enable creating a PDF or Microsoft file copy to upload to HubSpot")}
                </div>
              }
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

export default WorkflowOutputHubspotModal;
